import React, { useEffect, useState } from "react";
import {
  Pencil, ArrowLeftRight, Share2, Trash2, Check, X, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";
import { BottomSheet } from "@/components/BottomSheet";
import { api } from "@/lib/api";
import { shareWithIntro } from "@/lib/share";

/**
 * ListActionSheet — long-press menu for to-do lists.
 *
 * Actions:
 *   - Rename
 *   - Reorder (enters in-place reorder mode with ◀ / ▶ swap buttons)
 *   - Share (Smart Share — pipes the whole list contents with intro)
 *   - Delete (confirms; cascades to items)
 *
 * Why arrow-button reorder over true touch-drag?
 *   Reliable on iOS/Android, accessible (keyboard + screen reader), no
 *   gesture conflict with horizontal-scroll containers, and ships in one
 *   iteration. The "drag" gesture is implicit — user holds chip → menu →
 *   Reorder → taps arrows. Future iteration can layer real-drag on top
 *   without changing the contract.
 */
export function ListActionSheet({
  open, list, lists = [], familyId, onChanged, onClose,
}) {
  const [mode, setMode] = useState("menu"); // menu | rename | reorder
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) setMode("menu"); }, [open]);

  if (!open || !list) return null;

  const close = () => { setMode("menu"); onClose?.(); };
  const finished = () => { setMode("menu"); onChanged?.(); onClose?.(); };

  const doRename = async (name) => {
    setBusy(true);
    try {
      await api.patch(`/families/${familyId}/todo-lists/${list.id}`, { name });
      finished();
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm(`Delete "${list.name}" and all its items? This can't be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/families/${familyId}/todo-lists/${list.id}`);
      finished();
    } finally { setBusy(false); }
  };

  const doShare = async () => {
    try {
      // Fetch live items so the share contains the current contents,
      // not whatever the user last had cached.
      const { data: items } = await api.get(
        `/families/${familyId}/todo-lists/${list.id}/items`,
      );
      await shareWithIntro({ kind: "list", list, items: items || [] });
    } catch {
      await shareWithIntro({ kind: "list", list, items: [] });
    }
    close();
  };

  const reorderTo = async (orderedIds) => {
    setBusy(true);
    try {
      await api.post(`/families/${familyId}/todo-lists/reorder`, { list_ids: orderedIds });
      // Stay in reorder mode so the user can do multiple swaps — parent
      // refetches via onChanged so the chip ordering visibly updates.
      onChanged?.();
    } finally { setBusy(false); }
  };

  return (
    <BottomSheet open onClose={close} title={mode === "menu" ? list.name : null}>
      <div data-testid={`list-action-sheet-${list.id}`} className="pb-3">
        {mode === "menu" && (
          <ul className="divide-y divide-border/60">
            <ActionRow icon={Pencil} label="Rename list"
              testid="list-action-rename" onClick={() => setMode("rename")} />
            <ActionRow icon={ArrowLeftRight} label="Reorder lists"
              testid="list-action-reorder" onClick={() => setMode("reorder")} />
            <ActionRow icon={Share2} label="Share list"
              testid="list-action-share" onClick={doShare} />
            <ActionRow icon={Trash2} label="Delete list" destructive
              testid="list-action-delete" onClick={doDelete} busy={busy} />
          </ul>
        )}

        {mode === "rename" && (
          <RenameForm
            initial={list.name}
            busy={busy}
            onCancel={() => setMode("menu")}
            onSubmit={doRename}
          />
        )}

        {mode === "reorder" && (
          <ReorderList
            list={list}
            lists={lists}
            busy={busy}
            onReorder={reorderTo}
            onDone={() => setMode("menu")}
          />
        )}
      </div>
    </BottomSheet>
  );
}

function ActionRow({ icon: Icon, label, onClick, testid, destructive, busy }) {
  return (
    <li>
      <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        disabled={busy}
        className={[
          "w-full flex items-center gap-3 py-3.5 tap text-left disabled:opacity-50",
          destructive ? "text-destructive" : "text-foreground",
        ].join(" ")}
      >
        <Icon className="w-4 h-4 shrink-0" />
        <span className="text-[15px] font-medium">{label}</span>
        {busy && <Loader2 className="w-4 h-4 animate-spin ml-auto" />}
      </button>
    </li>
  );
}

function RenameForm({ initial, busy, onCancel, onSubmit }) {
  const [v, setV] = useState(initial || "");
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); if (v.trim()) onSubmit(v.trim()); }}
      className="space-y-4"
      data-testid="list-rename-form"
    >
      <input
        autoFocus
        data-testid="list-rename-input"
        value={v}
        onChange={(e) => setV(e.target.value)}
        className="w-full bg-transparent border-b-2 border-border focus:border-primary py-2 outline-none text-[16px]"
      />
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button" onClick={onCancel}
          className="h-11 rounded-2xl border border-border text-sm font-medium tap inline-flex items-center justify-center gap-1.5"
        >
          <X className="w-4 h-4" /> Cancel
        </button>
        <button
          type="submit" disabled={busy || !v.trim()}
          data-testid="list-rename-save"
          className="h-11 rounded-2xl bg-foreground text-background text-sm font-medium tap disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
        </button>
      </div>
    </form>
  );
}

function ReorderList({ list, lists, busy, onReorder, onDone }) {
  // Local mirror so the swap renders instantly; commit on every arrow tap.
  const [order, setOrder] = useState(() => lists.map((l) => l.id));

  useEffect(() => {
    setOrder(lists.map((l) => l.id));
  }, [lists]);

  const swap = (idx, dir) => {
    setOrder((cur) => {
      const next = [...cur];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[idx], next[j]] = [next[j], next[idx]];
      onReorder(next);
      return next;
    });
  };

  const byId = Object.fromEntries(lists.map((l) => [l.id, l]));

  return (
    <div className="space-y-3" data-testid="list-reorder-mode">
      <p className="text-[13px] text-muted-foreground">
        Tap the arrows to move a list. Order saves instantly.
      </p>
      <ul className="space-y-1.5 max-h-[55vh] overflow-y-auto">
        {order.map((id, idx) => {
          const item = byId[id];
          if (!item) return null;
          const isCurrent = item.id === list.id;
          return (
            <li
              key={id}
              data-testid={`list-reorder-row-${id}`}
              className={[
                "flex items-center gap-2 rounded-xl border px-2 py-2",
                isCurrent ? "border-primary/40 bg-primary/[0.06]" : "border-border bg-card",
              ].join(" ")}
            >
              <button
                type="button"
                data-testid={`list-reorder-up-${id}`}
                onClick={() => swap(idx, -1)}
                disabled={busy || idx === 0}
                className="w-9 h-9 rounded-full border border-border tap inline-flex items-center justify-center disabled:opacity-30"
                aria-label="Move left"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="flex-1 min-w-0 text-[14px] font-medium truncate">{item.name}</span>
              <button
                type="button"
                data-testid={`list-reorder-down-${id}`}
                onClick={() => swap(idx, 1)}
                disabled={busy || idx === order.length - 1}
                className="w-9 h-9 rounded-full border border-border tap inline-flex items-center justify-center disabled:opacity-30"
                aria-label="Move right"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        data-testid="list-reorder-done"
        onClick={onDone}
        className="w-full h-11 rounded-2xl bg-foreground text-background text-sm font-medium tap inline-flex items-center justify-center gap-1.5"
      >
        <Check className="w-4 h-4" /> Done
      </button>
    </div>
  );
}
